import React, { useContext, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ISuggestionForm } from '../../types/suggestions/form';
import DocumentUploadDropzone from '../common/DocumentUploadDropzone';
import FormField from '../common/FormField';
import { useDynamicFormState } from '../common/useDynamicFormState';
import { CurrentUserContext } from '../../context/CurrentUserContext';
import { postDocumentWithMetadata } from '../../services/documents/Documents';
import { useApiClient } from '../../context/ApiContext';
import { addListItem, IListItemReq, updateListItem, updateUserProfile } from '../../services/sharepoint/Sharepoint';
import {
    groupFieldsByDestination,
    inferDestinationsFromFormType,
    buildDocumentUploadRequest,
    buildUserProfileUpdateRequest,
    buildListItemUpdateRequest
} from './Utility';
import { ISuggestion, useSuggestions } from '../../context/SuggestionsContext';

interface SuggestionFormProps {
    form: ISuggestionForm;
    suggestion: ISuggestion;
    isOpen: boolean;
    onDismiss: () => void;
}

const SuggestionForm: React.FC<SuggestionFormProps> = ({ form, suggestion, isOpen, onDismiss }) => {
    const { currentUser } = useContext(CurrentUserContext);
    const { formState, updateField, resetForm, getFieldValue, hasFieldError, validateForm } = useDynamicFormState(form.fields, currentUser);
    const { dismissSuggestionById } = useSuggestions();

    const [uploadError, setUploadError] = useState<boolean>(false);
    const [files, setFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const apiClient = useApiClient();

    const onFilesSelected = (selectedFiles: File[]) => {
        setUploadError(false);
        setFiles(selectedFiles);
    };

    const formWithDestinations = React.useMemo(() => {
        const clonedForm: ISuggestionForm = {
            ...form,
            fields: form.fields.map(field => ({ ...field }))
        };
        inferDestinationsFromFormType(clonedForm);
        return clonedForm;
    }, [form]);

    const requiresFileUpload = formWithDestinations.fields.some(field =>
        field.destinations?.includes('DocumentLibrary')
    );

    const handleSubmit = () => {
        setIsLoading(true);

        let isValid = validateForm();

        if (requiresFileUpload && files.length === 0) {
            setUploadError(true);
            isValid = false;
        } else {
            setUploadError(false);
        }

        if (!isValid) {
            console.log('Form validation failed');
            setIsLoading(false);
            return;
        }

        console.log('Form is valid, submitting...', formState);

        const currentValuesArray = suggestion.CurrentValue
            ? suggestion.CurrentValue.split('|').map((v: string) => v.trim())
            : [];

        const fieldsByDestination = groupFieldsByDestination(
            formWithDestinations.fields,
            formState,
            currentUser,
            currentValuesArray
        );

        const requests: Promise<any>[] = [];

        if (fieldsByDestination.has('DocumentLibrary')) {
            const docFields = fieldsByDestination.get('DocumentLibrary')!;
            files.forEach(file => {
                const request = buildDocumentUploadRequest(file, docFields, formWithDestinations);
                console.log('Document upload request:', request);
                requests.push(postDocumentWithMetadata(apiClient, request));
            });
        }

        if (fieldsByDestination.has('UserProfile')) {
            const profileFields = fieldsByDestination.get('UserProfile')!;
            const request = buildUserProfileUpdateRequest(profileFields, currentUser);
            console.log('User profile update request:', request);
            requests.push(updateUserProfile(apiClient, request, 'POSITIVE\\' + currentUser.LoginName));
        }

        if (fieldsByDestination.has('ListItem')) {
            const listFields = fieldsByDestination.get('ListItem')!;
            const request = buildListItemUpdateRequest(
                listFields,
                formWithDestinations,
                currentUser,
                suggestion.CurrentValue || ''
            );
            console.log('List item update request:', request);

            if (formWithDestinations.type === 'AddListItem' || request.itemId === 0) {
                const addRequest: IListItemReq = {
                    siteUrl: request.siteUrl,
                    listName: request.listName,
                    fieldValues: request.fieldValues,
                    user: request.user
                };
                requests.push(addListItem(apiClient, addRequest));
            } else {
                requests.push(updateListItem(apiClient, request));
            }
        }

        if (fieldsByDestination.has('External') && formWithDestinations.api) {
            console.log('External API call not yet implemented', formWithDestinations.api);
        }
        //For testing uncomment loading code, comment the promise.all block

        //setIsLoading(false);

        const dismissNote = form.action ? `Action button '${form.action}' was selected` : 'Action Completed';

        Promise.all(requests)
            .then(() => dismissSuggestionById(suggestion.Id, dismissNote))
            .then(() => {
                console.log('Suggestion dismissed successfully');
                setIsLoading(false);
                onDismiss();
                resetForm();
            })
            .catch(error => {
                console.error('Error processing form submission', error);
                setIsLoading(false);
            });
    };

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                console.log('Dialog onOpenChange called - open:', open);
                if (!open) onDismiss();
            }}
        >
            <DialogContent className="max-w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto sm:min-w-[500px] sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="pr-6 break-words">{suggestion.Title}</DialogTitle>
                </DialogHeader>
                <div>
                    {requiresFileUpload && (
                        <div className="px-5">
                            <DocumentUploadDropzone
                                maxFiles={form.maxFiles}
                                fileTypes={form.fileTypes}
                                onFilesSelected={onFilesSelected}
                            />
                            {uploadError && (
                                <p className="text-sm text-destructive mt-1">
                                    Please upload at least one valid document.
                                </p>
                            )}
                        </div>
                    )}
                    {form.fields.map((field) => {
                        if (field.type === 'Internal') return null;
                        return (
                            <div key={field.id} className="px-5 pb-5">
                                <FormField
                                    field={field}
                                    value={getFieldValue(field.id)}
                                    onChange={(value) => updateField(field.id, value)}
                                    error={hasFieldError(field.id)}
                                />
                            </div>
                        );
                    })}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onDismiss} disabled={isLoading}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={isLoading}>
                        {isLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2" />
                        )}
                        {isLoading ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SuggestionForm;
